import { useCallback, useEffect, useState } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDate } from '../lib/util';
import { StatusBadge } from '../components/StatusBadge';
import { DeadlineIndicator } from '../components/DeadlineIndicator';
import { PagingControls } from '../components/PagingControls';
import { SearchFilterBar, useSearchFilter } from '../components/SearchFilterBar';
import { SettlementOpsPanel } from '../components/SettlementOpsPanel';
import { ConfirmationRecords } from '../components/ConfirmationRecords';
import { getCurrentUser } from '../auth/useUser';
import { useAppData } from '../state/contexts/AppDataContext';
import { useLifecycleActions } from '../state/actions/lifecycle';
import { unionMatching, useCaseQuery } from '../state/useCaseQuery';
import {
  getSettlementStatus,
  testSettlementConnection,
  type SettlementProbeResult,
  type SettlementStatus,
} from '../api/settlement';

// R10 (#78, resolves D7): read-only integration status card. Replaces the
// demo-era "API configuration" form — the SPA never collects or displays
// credentials; the status endpoint returns the endpoint HOST and presence
// booleans only.
function SettlementStatusCard({ pendingCount }: { pendingCount: number }) {
  const [status, setStatus] = useState<SettlementStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [probe, setProbe] = useState<SettlementProbeResult | null>(null);
  const [probing, setProbing] = useState(false);

  // Loads once on mount and re-loads on every pendingCount change (including
  // the drop to 0, which catches the final SYNCED state); while syncs are
  // pending it also polls so the card updates without a manual reload.
  useEffect(() => {
    let cancelled = false;
    const load = () =>
      getSettlementStatus()
        .then((s) => { if (!cancelled) { setStatus(s); setStatusError(null); } })
        .catch((err: unknown) => { if (!cancelled) setStatusError(err instanceof Error ? err.message : String(err)); });
    load();
    if (pendingCount === 0) return () => { cancelled = true; };
    const timer = setInterval(load, 10_000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [pendingCount]);

  const runProbe = useCallback(() => {
    setProbing(true);
    setProbe(null);
    testSettlementConnection()
      .then(setProbe)
      .catch((err: unknown) => setProbe({ ok: false, mode: status?.mode ?? 'STUB', detail: err instanceof Error ? err.message : String(err) }))
      .finally(() => setProbing(false));
  }, [status?.mode]);

  const field = (label: string, value: React.ReactNode) => (
    <div>
      <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: C.text }}>{value}</div>
    </div>
  );
  const configured = (isSet: boolean) =>
    isSet
      ? <span style={{ color: C.success }}>configured</span>
      : <span style={{ color: C.warn }}>not configured</span>;

  return (
    <div style={{ ...styles.card, background: C.bgInput }} data-testid="settlement-status-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600 }}>CTIA BCID Platform Integration</h3>
        {status && (
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
            color: status.mode === 'LIVE' ? C.success : C.warn,
            border: `1px solid ${status.mode === 'LIVE' ? C.success : C.warn}`,
          }}>{status.mode}</span>
        )}
      </div>
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>
        Read-only status — credentials are held server-side (Key Vault) and are never shown or collected here.
      </div>
      {statusError && <div style={{ fontSize: 12, color: C.danger }}>Status unavailable: {statusError}</div>}
      {status && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {field('API endpoint host', status.endpointHost ?? <span style={{ color: C.warn }}>not configured</span>)}
          {field('OA token (Enterprise Account API)', configured(status.oaTokenConfigured))}
          {field('VA token (Data Upload API)', configured(status.vaTokenConfigured))}
          {field('VAID (Vetting Agent ID)', configured(status.vaidConfigured))}
          {field('Default OSPIDs', configured(status.ospidsConfigured))}
          {field('Last successful sync', status.lastSuccessfulSync ? fmtDate(status.lastSuccessfulSync) : '—')}
          {field('Pending sync', String(status.pendingSyncCount))}
          {field('Open dead letters', `${status.deadLetterCount}${status.lastDeadLetterAt ? ` (last ${fmtDate(status.lastDeadLetterAt)})` : ''}`)}
          {field('Display confirmations', `${status.confirmationCount}${status.lastConfirmedAt ? ` (last ${fmtDate(status.lastConfirmedAt)})` : ''}`)}
        </div>
      )}
      <button
        style={{ ...styles.btn, ...styles.btnOutline, marginTop: 16 }}
        disabled={probing}
        onClick={runProbe}
      >{probing ? 'Testing…' : 'Test Connection'}</button>
      {probe && (
        <div style={{ marginTop: 10, fontSize: 12, color: probe.ok ? C.success : C.danger }}>
          {probe.ok ? '✓ Connection OK — ' : '✗ Connection failed — '}{probe.detail}
        </div>
      )}
    </div>
  );
}

// The values `cases.settlement_sync_status` can actually hold: the CHECK
// constraint `cases_settlement_sync_status_check` in
// apps/api/src/db/schema/cases.ts (NULL aside). A failed delivery is NOT a
// sync status — it is a row in `settlement_dead_letter` with the case still
// PENDING_SYNC — so a "Sync Failed" option here could never match a row
// (#760). Tied to the API's list by a disk-read pin in Settlement.test.tsx —
// "offers exactly the sync statuses the cases CHECK constraint allows, read
// out of the API source"; see docs/CROSS_PACKAGE_CONSTANTS.md.
export const SETTLEMENT_SYNC_STATUS_OPTIONS = [
  { value: 'SYNCED', label: 'Synced' },
  { value: 'PENDING_SYNC', label: 'Pending Sync' },
] as const;

export function Settlement() {
  const { cases, now, stats } = useAppData();
  const { syncToSettlement } = useLifecycleActions();

  // H-2: pending-sync work and the vetted-registrant DB query the full
  // visible population server-side; the stat boxes come from the same
  // server aggregate as the Dashboard tiles so counts can't disagree with
  // what the lists surface.
  const pendingQuery = useCaseQuery({ settlementSyncStatus: 'PENDING_SYNC' });
  const vettedQuery = useCaseQuery({ hasEid: true });

  const pendingSync = unionMatching(pendingQuery.cases, cases, c => c.settlementSyncStatus === 'PENDING_SYNC');
  const vetted = unionMatching(vettedQuery.cases, cases, c => Boolean(c.eid), vettedQuery.offset);

  // Client-side v1 search over the in-memory rows the per-section server
  // queries already returned (server-side filtering is a deferred phase 2).
  // The sync-status facet only applies to the vetted-registrant table; the
  // pending-sync card is already scoped to PENDING_SYNC, so the facet leaves
  // it untouched.
  const rows = [
    ...pendingSync.map(c => ({ c, section: 'pending' as const })),
    ...vetted.map(c => ({ c, section: 'vetted' as const })),
  ];
  const { filtered: filteredRows, bar } = useSearchFilter(
    rows,
    ({ c }) => [c.id, c.registrant, c.eid, c.settlementSyncStatus].filter(Boolean).join(' '),
    [
      {
        id: 'syncStatus',
        label: 'Sync status',
        options: [
          { value: '', label: 'All sync statuses' },
          ...SETTLEMENT_SYNC_STATUS_OPTIONS,
        ],
        predicate: ({ c, section }, v) => section !== 'vetted' || c.settlementSyncStatus === v,
      },
    ],
    { placeholder: 'Search by case, registrant, or EID…' },
  );
  const filteredPending = filteredRows.filter(r => r.section === 'pending').map(r => r.c);
  const filteredVetted = filteredRows.filter(r => r.section === 'vetted').map(r => r.c);

  // Live refresh while syncs are in flight: the worker owns the SYNCED
  // transition, so re-fetch the queries every 10s until nothing is pending —
  // refetch merges into the store, which drops completed rows from the
  // pending card and updates the vetted table without a reload.
  const hasPending = pendingSync.length > 0;
  const refetchPending = pendingQuery.refetch;
  const refetchVetted = vettedQuery.refetch;
  useEffect(() => {
    if (!hasPending) return;
    const timer = setInterval(() => {
      refetchPending();
      refetchVetted();
    }, 10_000);
    return () => clearInterval(timer);
  }, [hasPending, refetchPending, refetchVetted]);

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 4 }}>Settlement Platform Integration</h2>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 20 }}>EID management · Master Database sync · 24-hour change deadline</div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <div style={{ ...styles.statBox, borderLeft: `3px solid ${C.success}` }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: C.success }}>{stats.synced}</div>
          <div style={{ fontSize: 11, color: C.textMuted }}>Synced</div>
        </div>
        <div style={{ ...styles.statBox, borderLeft: `3px solid ${C.warn}` }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: C.warn }}>{stats.pendingSync}</div>
          <div style={{ fontSize: 11, color: C.textMuted }}>Pending Sync</div>
        </div>
      </div>
      {(pendingSync.length > 0 || vetted.length > 0) && <SearchFilterBar {...bar} />}
      {pendingSync.length > 0 && (
        <div style={{ ...styles.card, borderLeft: `3px solid ${C.warn}` }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: C.warn, marginBottom: 12 }}>⚠ Pending Sync</h3>
          {filteredPending.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
              <div>
                <strong style={{ color: C.goldText }}>{c.id}</strong> — {c.registrant}
                <div style={{ fontSize: 12, color: C.textMuted }}>EID: <span style={{ fontFamily: 'monospace' }}>{c.eid || 'To be generated'}</span></div>
                {c.settlementChangeDeadline && <DeadlineIndicator deadline={c.settlementChangeDeadline} label="24hr sync deadline" now={now} />}
              </div>
              <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => syncToSettlement(c.id)}>Sync to Settlement Platform</button>
            </div>
          ))}
          {filteredPending.length === 0 && (
            <div style={{ fontSize: 13, color: C.textMuted }}>No pending syncs match the current filters</div>
          )}
        </div>
      )}
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Vetted Registrant Database</h3>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>All vetted BCID customers with EIDs synced to Settlement Platform Master Database</div>
        <table style={styles.table}>
          <thead><tr>
            <th style={styles.th}>EID</th><th style={styles.th}>Registrant</th><th style={styles.th}>Status</th><th style={styles.th}>Last Sync</th><th style={styles.th}>Sync Status</th>
          </tr></thead>
          <tbody>
            {filteredVetted.map(c => (
              <tr key={c.id}>
                <td style={styles.td}><span style={{ fontFamily: 'monospace', fontSize: 12, color: C.goldText, fontWeight: 600 }}>{c.eid}</span></td>
                <td style={styles.td}>{c.registrant}</td>
                <td style={styles.td}><StatusBadge status={c.status} /></td>
                <td style={styles.td}>{fmtDate(c.settlementLastSync)}</td>
                <td style={styles.td}><StatusBadge status={c.settlementSyncStatus || '—'} /></td>
              </tr>
            ))}
            {filteredVetted.length === 0 && (
              <tr><td colSpan={5} style={{ ...styles.td, color: C.textMuted, textAlign: 'center' }}>{vetted.length === 0 ? 'No vetted registrants' : 'No registrants match the current filters'}</td></tr>
            )}
          </tbody>
        </table>
        <PagingControls offset={vettedQuery.offset} limit={vettedQuery.limit} total={vettedQuery.total} count={vetted.length} onOffset={vettedQuery.setOffset} label="registrants" />
      </div>
      <SettlementStatusCard pendingCount={pendingSync.length} />
      <ConfirmationRecords />
      {getCurrentUser()?.canAdmin && <SettlementOpsPanel />}
    </div>
  );
}
